import { useEffect, useState } from "react";
import { Tabs } from "@mantine/core";
import { useNavigate, useParams } from "react-router";
import styled from "styled-components";
import PageHeader from "../components/common/PageHeader";
import Container from "../components/common/Container";
import ContainerLoadingOverlay from "../components/common/ContainerLoadingOverlay";
import ShiftDefinitionForm, {
  ShiftDefinitionFormValues,
  daysFromRows,
} from "../components/WorkShift/ShiftDefinitionForm";
import ShiftEmployeesTable from "../components/WorkShift/ShiftEmployeesTable";
import {
  ShiftDefinition,
  getShiftDefinitionById,
  updateShiftDefinition,
} from "../api/shiftDefinitionApi";
import { createNotification } from "../utils/useNotification";

/**
 * Vardiya tanımı detayı (plan 2.24).
 *
 * Eski üç sekme ("Vardiya Bilgileri" / "Aktif Çalışanlar" / "Tüm Çalışanlar")
 * ikiye indi: yeni modelde atamalar tarih aralıklı olduğu için "aktif" ile
 * "tüm" ayrımının karşılığı yok — tek bir tarih seçici ikisini de veriyor.
 */
const WorkShiftDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<string | null>("detail");
  const [definition, setDefinition] = useState<ShiftDefinition | null>(null);

  useEffect(() => {
    if (!id) return;
    getShiftDefinitionById(id)
      .then((data: any) => setDefinition(data?.response ?? null))
      .catch(() => setDefinition(null))
      .finally(() => setIsLoading(false));
  }, [id]);

  const handleSubmit = async (values: ShiftDefinitionFormValues) => {
    if (!id) return;
    const response: any = await updateShiftDefinition(id, {
      name: values.name.trim(),
      code: values.code.trim(),
      departmentIds: values.departmentIds,
      days: daysFromRows(values.days),
      // Boş string bilerek gönderiliyor: "kesimi temizle, departmanınki geçerli
      // olsun" demenin yolu bu (backend `undefined` ile ayırıyor).
      dayCutoff: values.dayCutoff,
      lateBufferMinutes:
        values.lateBufferMinutes === "" ? 0 : Number(values.lateBufferMinutes),
      earlyLeaveBufferMinutes:
        values.earlyLeaveBufferMinutes === ""
          ? 0
          : Number(values.earlyLeaveBufferMinutes),
      notes: values.notes,
    });

    createNotification({
      success: response?.success,
      message: response?.success
        ? "Vardiya güncellendi."
        : response?.message ?? "Vardiya güncellenemedi.",
    });
  };

  if (isLoading) return <ContainerLoadingOverlay visible />;

  if (!definition) {
    return (
      <Container>
        <PageHeader pageTitle="Vardiya bulunamadı" />
        <NotFound onClick={() => navigate("/shifts/work-shift")}>
          Vardiya listesine dön
        </NotFound>
      </Container>
    );
  }

  return (
    <Container>
      <PageHeader
        pageTitle={`${definition.code} · ${definition.name}`}
        subText="Haftalık çalışma düzeni tanımı"
      />
      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List mb={20} w="max-content">
          <StyledTab value="detail">Vardiya Bilgileri</StyledTab>
          <StyledTab value="employees">Çalışanlar</StyledTab>
        </Tabs.List>

        <Tabs.Panel value="detail">
          <ShiftDefinitionForm
            initial={definition}
            submitLabel="Güncelle"
            onSubmit={handleSubmit}
          />
        </Tabs.Panel>

        <Tabs.Panel value="employees">
          <ShiftEmployeesTable shiftDefinitionId={definition._id} />
        </Tabs.Panel>
      </Tabs>
    </Container>
  );
};

export default WorkShiftDetail;

const StyledTab = styled(Tabs.Tab)`
  color: #9ca3af;
  &[aria-selected="true"] {
    color: #5c26ff;
    border-color: #5c26ff;
    border-bottom-width: 3px;
  }
`;

const NotFound = styled.button`
  background: none;
  border: none;
  color: #5c26ff;
  cursor: pointer;
  font-size: 15px;
  padding: 0;
`;
